import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import {
  BookOpen, Users, Plus, Video, FileText, ArrowLeft, Star,
  BarChart2, Eye, Edit, DollarSign, X, CheckCircle2,
  LayoutDashboard, Bell, Settings, Clock, MessageSquare,
  PlayCircle, Award, Save, LogOut, GraduationCap, Menu
} from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { Label } from '@/components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Badge } from '@/components/ui/badge';
import { BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, LineChart, Line, AreaChart, Area } from 'recharts';
import { COURSES, clearUser } from '@/lib/data';
import ModuleManager from '@/components/instructor/ModuleManager';
import StudentAccessPanel from '@/components/instructor/StudentAccessPanel';
import InstructorNotifications from '@/components/instructor/InstructorNotifications';

const enrollmentData = [
  { month: 'Jan', students: 120 }, { month: 'Feb', students: 180 },
  { month: 'Mar', students: 250 }, { month: 'Apr', students: 310 },
  { month: 'May', students: 280 }, { month: 'Jun', students: 420 },
];
const revenueData = [
  { month: 'Jan', revenue: 142500 }, { month: 'Feb', revenue: 198000 },
  { month: 'Mar', revenue: 265000 }, { month: 'Apr', revenue: 381500 },
  { month: 'May', revenue: 312000 }, { month: 'Jun', revenue: 504000 },
];
const completionData = [
  { week: 'W1', rate: 92 }, { week: 'W2', rate: 85 },
  { week: 'W3', rate: 78 }, { week: 'W4', rate: 88 },
  { week: 'W5', rate: 94 }, { week: 'W6', rate: 90 },
];
const courseStudents = [
  { name: 'Aisha Patel', email: 'aisha@email.com', progress: 78, enrolled: '2026-01-10', lastActive: '2 hours ago', status: 'active' },
  { name: 'Marcus Johnson', email: 'marcus@email.com', progress: 45, enrolled: '2026-01-22', lastActive: '1 day ago', status: 'active' },
  { name: 'Lisa Chang', email: 'lisa@email.com', progress: 100, enrolled: '2025-12-15', lastActive: '3 days ago', status: 'completed' },
  { name: 'Raj Gupta', email: 'raj@email.com', progress: 20, enrolled: '2026-02-01', lastActive: '5 days ago', status: 'inactive' },
  { name: 'Sophie Martinez', email: 'sophie@email.com', progress: 62, enrolled: '2026-01-05', lastActive: '4 hours ago', status: 'active' },
];
const courseReviews = [
  { name: 'Aisha Patel', rating: 5, text: 'Best course I have ever taken. Very practical and project-focused!', date: '2 weeks ago' },
  { name: 'Marcus Johnson', rating: 4, text: 'Great content, the instructor explains everything clearly.', date: '1 month ago' },
  { name: 'Sophie Martinez', rating: 5, text: 'Incredible value for money. Got placed within 2 months!', date: '3 weeks ago' },
];

const NAV = [
  { id: 'overview', label: 'Overview', icon: LayoutDashboard },
  { id: 'courses', label: 'My Courses', icon: BookOpen },
  { id: 'students', label: 'Students', icon: Users },
  { id: 'analytics', label: 'Analytics', icon: BarChart2 },
  { id: 'notifications', label: 'Notifications', icon: Bell },
  { id: 'settings', label: 'Settings', icon: Settings },
];

export default function InstructorDashboard() {
  const [section, setSection] = useState('overview');
  const [showForm, setShowForm] = useState(false);
  const [selectedCourse, setSelectedCourse] = useState(null);
  const [courseView, setCourseView] = useState('detail'); // 'detail' | 'modules' | 'access'
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const [unread] = useState(3);

  const myCourses = COURSES.slice(0, 4);
  const stats = [
    { label: 'Total Students', value: '1,560', icon: Users, color: 'text-primary bg-primary/10', change: '+48 this month' },
    { label: 'Active Courses', value: myCourses.length.toString(), icon: BookOpen, color: 'text-accent bg-accent/10', change: '2 pending review' },
    { label: 'Revenue', value: '₹55.3L', icon: DollarSign, color: 'text-green-600 bg-green-100', change: '+₹4.2L this month' },
    { label: 'Avg Rating', value: '4.8 ★', icon: Star, color: 'text-amber-600 bg-amber-100', change: 'From 1,240 reviews' },
    { label: 'Completion Rate', value: '72%', icon: Award, color: 'text-purple-600 bg-purple-100', change: '+5% vs last month' },
    { label: 'Avg Watch Time', value: '4.2h', icon: Clock, color: 'text-cyan-600 bg-cyan-100', change: 'Per student/week' },
  ];

  const navigate = (id) => { setSection(id); setSelectedCourse(null); setSidebarOpen(false); };

  const SidebarContent = () => (
    <>
      <div className="p-5 border-b">
        <div className="flex items-center gap-2 mb-2">
          <div className="w-8 h-8 bg-primary rounded-lg flex items-center justify-center">
            <GraduationCap className="w-4 h-4 text-primary-foreground" />
          </div>
          <span className="font-heading font-bold">XceedEdge</span>
        </div>
        <Badge className="text-xs bg-purple-100 text-purple-700 border-0">Instructor</Badge>
      </div>
      <nav className="flex-1 p-3 space-y-0.5 overflow-y-auto">
        {NAV.map(item => {
          const Icon = item.icon;
          return (
            <button key={item.id} onClick={() => navigate(item.id)}
              className={`w-full flex items-center gap-3 px-3 py-2.5 rounded-lg text-sm font-medium transition-all relative ${
                section === item.id ? 'bg-primary text-primary-foreground' : 'text-muted-foreground hover:bg-muted hover:text-foreground'
              }`}>
              <Icon className="w-4 h-4" />
              {item.label}
              {item.id === 'notifications' && unread > 0 && (
                <span className="ml-auto text-xs bg-red-500 text-white rounded-full w-4 h-4 flex items-center justify-center leading-none">{unread}</span>
              )}
            </button>
          );
        })}
      </nav>
      <div className="p-3 border-t space-y-1">
        <Link to="/" className="flex items-center gap-3 px-3 py-2.5 rounded-lg text-sm font-medium text-muted-foreground hover:bg-muted hover:text-foreground">
          <ArrowLeft className="w-4 h-4" /> View Site
        </Link>
        <button onClick={() => { clearUser(); window.location.href = '/'; }}
          className="w-full flex items-center gap-3 px-3 py-2.5 rounded-lg text-sm font-medium text-muted-foreground hover:bg-muted hover:text-foreground">
          <LogOut className="w-4 h-4" /> Logout
        </button>
      </div>
    </>
  );

  return (
    <div className="min-h-screen bg-background flex">
      {/* Desktop Sidebar */}
      <aside className="hidden lg:flex w-60 bg-card border-r flex-col fixed top-0 left-0 bottom-0 z-40">
        <SidebarContent />
      </aside>

      {/* Mobile Sidebar */}
      <AnimatePresence>
        {sidebarOpen && (
          <>
            <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}
              className="lg:hidden fixed inset-0 bg-foreground/40 z-40" onClick={() => setSidebarOpen(false)} />
            <motion.aside initial={{ x: -260 }} animate={{ x: 0 }} exit={{ x: -260 }}
              className="lg:hidden fixed top-0 left-0 bottom-0 w-60 bg-card z-50 border-r flex flex-col">
              <SidebarContent />
            </motion.aside>
          </>
        )}
      </AnimatePresence>

      {/* Main */}
      <div className="flex-1 lg:ml-60">
        {/* Mobile header */}
        <div className="lg:hidden sticky top-0 z-30 bg-card/95 backdrop-blur border-b px-4 py-3 flex items-center justify-between">
          <Button variant="ghost" size="icon" onClick={() => setSidebarOpen(true)}><Menu className="w-5 h-5" /></Button>
          <span className="font-heading font-bold">Instructor</span>
          <Button variant="ghost" size="icon" onClick={() => navigate('notifications')}>
            <Bell className="w-5 h-5" />
            {unread > 0 && <span className="absolute top-1 right-1 w-2 h-2 bg-red-500 rounded-full" />}
          </Button>
        </div>

        <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} className="p-4 sm:p-6 lg:p-8 space-y-6 max-w-6xl">

          {/* ===== OVERVIEW ===== */}
          {section === 'overview' && (
            <>
              <div className="flex items-center justify-between">
                <div>
                  <h1 className="font-heading text-2xl font-bold">Dashboard Overview</h1>
                  <p className="text-muted-foreground text-sm mt-0.5">Welcome back! Here's how your courses are performing.</p>
                </div>
                <Button onClick={() => setShowForm(true)} className="gap-2 hidden sm:flex">
                  <Plus className="w-4 h-4" /> Create Course
                </Button>
              </div>

              <div className="grid grid-cols-2 lg:grid-cols-3 gap-4">
                {stats.map(stat => {
                  const Icon = stat.icon;
                  return (
                    <div key={stat.label} className="bg-card rounded-xl border p-4">
                      <div className={`w-10 h-10 rounded-lg ${stat.color} flex items-center justify-center mb-3`}><Icon className="w-5 h-5" /></div>
                      <p className="font-heading text-2xl font-bold">{stat.value}</p>
                      <p className="text-xs text-muted-foreground">{stat.label}</p>
                      <p className="text-xs text-primary mt-1">{stat.change}</p>
                    </div>
                  );
                })}
              </div>

              {/* Quick course list */}
              <div className="bg-card rounded-xl border p-5">
                <div className="flex items-center justify-between mb-4">
                  <h3 className="font-heading font-semibold">My Courses</h3>
                  <button onClick={() => setSection('courses')} className="text-xs text-primary hover:underline">View all →</button>
                </div>
                <div className="space-y-3">
                  {myCourses.slice(0, 3).map(course => (
                    <div key={course.id} className="flex items-center gap-3 p-3 bg-muted/30 rounded-lg">
                      <div className="flex-1 min-w-0">
                        <p className="font-medium text-sm truncate">{course.title}</p>
                        <p className="text-xs text-muted-foreground">{course.students.toLocaleString()} students · {course.duration}</p>
                      </div>
                      <div className="flex items-center gap-1 text-xs">
                        <Star className="w-3 h-3 fill-amber-400 text-amber-400" />{course.rating}
                      </div>
                      <Badge variant="secondary" className="text-xs">{course.level}</Badge>
                      <Button size="sm" variant="ghost" className="h-7 text-xs" onClick={() => { setSelectedCourse(course); setSection('courses'); }}>
                        <Eye className="w-3 h-3" />
                      </Button>
                    </div>
                  ))}
                </div>
              </div>

              {/* Charts */}
              <div className="grid lg:grid-cols-2 gap-6">
                <div className="bg-card rounded-xl border p-5">
                  <h3 className="font-heading font-semibold mb-4">Enrollment Trend</h3>
                  <div className="h-52">
                    <ResponsiveContainer width="100%" height="100%">
                      <BarChart data={enrollmentData}>
                        <CartesianGrid strokeDasharray="3 3" stroke="hsl(var(--border))" />
                        <XAxis dataKey="month" tick={{ fontSize: 11 }} />
                        <YAxis tick={{ fontSize: 11 }} />
                        <Tooltip />
                        <Bar dataKey="students" fill="hsl(var(--primary))" radius={[4, 4, 0, 0]} />
                      </BarChart>
                    </ResponsiveContainer>
                  </div>
                </div>
                <div className="bg-card rounded-xl border p-5">
                  <h3 className="font-heading font-semibold mb-4">Revenue (₹)</h3>
                  <div className="h-52">
                    <ResponsiveContainer width="100%" height="100%">
                      <LineChart data={revenueData}>
                        <CartesianGrid strokeDasharray="3 3" stroke="hsl(var(--border))" />
                        <XAxis dataKey="month" tick={{ fontSize: 11 }} />
                        <YAxis tick={{ fontSize: 11 }} tickFormatter={v => `₹${(v/1000).toFixed(0)}K`} />
                        <Tooltip formatter={v => [`₹${v.toLocaleString('en-IN')}`, 'Revenue']} />
                        <Line type="monotone" dataKey="revenue" stroke="hsl(var(--accent))" strokeWidth={2} dot={{ r: 3 }} />
                      </LineChart>
                    </ResponsiveContainer>
                  </div>
                </div>
              </div>
            </>
          )}

          {/* ===== COURSES ===== */}
          {section === 'courses' && !selectedCourse && (
            <>
              <div className="flex items-center justify-between">
                <h1 className="font-heading text-2xl font-bold">My Courses</h1>
                <Button onClick={() => setShowForm(true)} className="gap-2"><Plus className="w-4 h-4" /> Create Course</Button>
              </div>
              <div className="grid sm:grid-cols-2 gap-4">
                {myCourses.map(course => (
                  <div key={course.id} className="bg-card rounded-xl border p-5 hover:shadow-md transition-shadow">
                    <div className="flex items-start justify-between gap-3 mb-3">
                      <div className="flex-1 min-w-0">
                        <h3 className="font-semibold text-sm">{course.title}</h3>
                        <div className="flex items-center gap-2 mt-1">
                          <Badge variant="secondary" className="text-xs">{course.level}</Badge>
                          <span className="text-xs text-muted-foreground">{course.duration}</span>
                        </div>
                      </div>
                      <p className="font-bold text-green-600 text-sm flex-shrink-0">₹{course.price.toLocaleString('en-IN')}</p>
                    </div>
                    <div className="flex items-center gap-4 text-xs text-muted-foreground mb-4">
                      <span className="flex items-center gap-1"><Users className="w-3 h-3" /> {course.students.toLocaleString()}</span>
                      <span className="flex items-center gap-1"><Star className="w-3 h-3 fill-amber-400 text-amber-400" /> {course.rating}</span>
                      <span className="flex items-center gap-1"><Clock className="w-3 h-3" /> {course.duration}</span>
                    </div>
                    <div className="flex gap-2">
                      <Button size="sm" className="gap-1.5 flex-1" onClick={() => { setSelectedCourse(course); setCourseView('detail'); }}>
                        <Eye className="w-3.5 h-3.5" /> View Details
                      </Button>
                      <Button size="sm" variant="outline" className="gap-1.5" onClick={() => { setSelectedCourse(course); setCourseView('modules'); }}>
                        <Edit className="w-3.5 h-3.5" /> Manage Modules
                      </Button>
                    </div>
                  </div>
                ))}
              </div>
            </>
          )}

          {/* Course detail / module manager / access panel */}
          {section === 'courses' && selectedCourse && (
            <>
              {courseView === 'modules' ? (
                <ModuleManager course={selectedCourse} onBack={() => setCourseView('detail')} />
              ) : courseView === 'access' ? (
                <>
                  <button onClick={() => setCourseView('detail')} className="text-sm text-muted-foreground hover:text-foreground flex items-center gap-1 mb-4">← Back</button>
                  <StudentAccessPanel course={selectedCourse} />
                </>
              ) : (
                <>
                  <div className="flex items-center gap-3 mb-2">
                    <button onClick={() => setSelectedCourse(null)} className="text-sm text-muted-foreground hover:text-foreground flex items-center gap-1">← Back to courses</button>
                  </div>
                  <div className="flex items-start justify-between gap-3 mb-4">
                    <div>
                      <h2 className="font-heading text-xl font-bold">{selectedCourse.title}</h2>
                      <div className="flex items-center gap-3 mt-1">
                        <Badge variant="secondary">{selectedCourse.level}</Badge>
                        <span className="text-sm text-muted-foreground">{selectedCourse.duration}</span>
                        <span className="text-sm text-muted-foreground">{selectedCourse.students.toLocaleString()} students</span>
                      </div>
                    </div>
                    <div className="text-right flex-shrink-0">
                      <p className="font-heading text-2xl font-bold text-green-600">₹{selectedCourse.price.toLocaleString('en-IN')}</p>
                      <p className="text-sm text-muted-foreground line-through">₹{selectedCourse.originalPrice.toLocaleString('en-IN')}</p>
                    </div>
                  </div>

                  {/* Action buttons */}
                  <div className="flex flex-wrap gap-2 mb-4">
                    <Button variant="outline" className="gap-2" onClick={() => setCourseView('modules')}>
                      <Edit className="w-4 h-4" /> Manage Modules
                    </Button>
                    <Button variant="outline" className="gap-2" onClick={() => setCourseView('access')}>
                      <Users className="w-4 h-4" /> Student Access
                    </Button>
                  </div>

                  <Tabs defaultValue="students">
                    <TabsList className="flex-wrap">
                      <TabsTrigger value="students" className="gap-1.5"><Users className="w-3.5 h-3.5" /> Students</TabsTrigger>
                      <TabsTrigger value="analytics" className="gap-1.5"><BarChart2 className="w-3.5 h-3.5" /> Analytics</TabsTrigger>
                      <TabsTrigger value="curriculum" className="gap-1.5"><PlayCircle className="w-3.5 h-3.5" /> Curriculum</TabsTrigger>
                      <TabsTrigger value="reviews" className="gap-1.5"><MessageSquare className="w-3.5 h-3.5" /> Reviews</TabsTrigger>
                    </TabsList>

                    <TabsContent value="students" className="mt-4 overflow-x-auto">
                      <table className="w-full text-sm">
                        <thead>
                          <tr className="border-b">
                            {['Student', 'Progress', 'Enrolled', 'Last Active', 'Status'].map(h => (
                              <th key={h} className="text-left pb-2 text-muted-foreground font-medium pr-4">{h}</th>
                            ))}
                          </tr>
                        </thead>
                        <tbody className="divide-y">
                          {courseStudents.map((s, i) => (
                            <tr key={i}>
                              <td className="py-3 pr-4">
                                <p className="font-medium">{s.name}</p>
                                <p className="text-xs text-muted-foreground">{s.email}</p>
                              </td>
                              <td className="py-3 pr-4">
                                <div className="flex items-center gap-2">
                                  <div className="w-20 h-1.5 bg-muted rounded-full overflow-hidden">
                                    <div className="h-full bg-primary rounded-full" style={{ width: `${s.progress}%` }} />
                                  </div>
                                  <span className="text-xs font-medium">{s.progress}%</span>
                                </div>
                              </td>
                              <td className="py-3 pr-4 text-muted-foreground">{new Date(s.enrolled).toLocaleDateString()}</td>
                              <td className="py-3 pr-4 text-muted-foreground">{s.lastActive}</td>
                              <td className="py-3">
                                <Badge className={`text-xs border-0 ${s.status === 'completed' ? 'bg-green-100 text-green-700' : s.status === 'inactive' ? 'bg-red-100 text-red-700' : 'bg-blue-100 text-blue-700'}`}>{s.status}</Badge>
                              </td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </TabsContent>

                    <TabsContent value="analytics" className="mt-4 space-y-4">
                      <div className="grid sm:grid-cols-3 gap-4">
                        {[
                          { label: 'Total Revenue', value: `₹${(selectedCourse.students * selectedCourse.price * 0.08).toLocaleString('en-IN', { maximumFractionDigits: 0 })}`, color: 'text-green-600' },
                          { label: 'Completion Rate', value: '72%', color: 'text-primary' },
                          { label: 'Avg. Rating', value: `${selectedCourse.rating} ★`, color: 'text-amber-600' },
                        ].map(m => (
                          <div key={m.label} className="bg-muted/50 rounded-lg p-4 text-center">
                            <p className={`font-heading text-2xl font-bold ${m.color}`}>{m.value}</p>
                            <p className="text-xs text-muted-foreground mt-1">{m.label}</p>
                          </div>
                        ))}
                      </div>
                      <div className="bg-muted/30 rounded-lg p-4">
                        <h4 className="text-sm font-semibold mb-3">Weekly Completion Rate</h4>
                        <div className="h-48">
                          <ResponsiveContainer width="100%" height="100%">
                            <AreaChart data={completionData}>
                              <CartesianGrid strokeDasharray="3 3" stroke="hsl(var(--border))" />
                              <XAxis dataKey="week" tick={{ fontSize: 11 }} />
                              <YAxis tick={{ fontSize: 11 }} domain={[60, 100]} />
                              <Tooltip formatter={v => `${v}%`} />
                              <Area type="monotone" dataKey="rate" stroke="hsl(var(--primary))" fill="hsl(var(--primary) / 0.1)" />
                            </AreaChart>
                          </ResponsiveContainer>
                        </div>
                      </div>
                    </TabsContent>

                    <TabsContent value="curriculum" className="mt-4 space-y-2">
                      {selectedCourse.curriculum.map((mod, i) => (
                        <div key={i} className="flex items-center justify-between bg-muted/30 rounded-lg p-3">
                          <div className="flex items-center gap-3">
                            <div className="w-8 h-8 rounded-lg bg-primary/10 flex items-center justify-center text-xs font-bold text-primary">{i + 1}</div>
                            <div>
                              <p className="text-sm font-medium">{mod.module}</p>
                              <p className="text-xs text-muted-foreground">{mod.lessons} lessons · {mod.duration}</p>
                            </div>
                          </div>
                          <Button variant="outline" size="sm" className="gap-1.5 text-xs" onClick={() => setCourseView('modules')}>
                            <Edit className="w-3 h-3" /> Edit
                          </Button>
                        </div>
                      ))}
                      <Button variant="outline" className="w-full gap-2 border-dashed" onClick={() => setCourseView('modules')}>
                        <Plus className="w-4 h-4" /> Manage All Modules
                      </Button>
                    </TabsContent>

                    <TabsContent value="reviews" className="mt-4 space-y-3">
                      {courseReviews.map((r, i) => (
                        <div key={i} className="bg-muted/30 rounded-lg p-4">
                          <div className="flex items-center justify-between mb-1">
                            <p className="font-medium text-sm">{r.name}</p>
                            <span className="text-xs text-muted-foreground">{r.date}</span>
                          </div>
                          <div className="flex gap-0.5 mb-2">
                            {[...Array(r.rating)].map((_, j) => <Star key={j} className="w-3 h-3 fill-amber-400 text-amber-400" />)}
                          </div>
                          <p className="text-sm text-muted-foreground">{r.text}</p>
                        </div>
                      ))}
                    </TabsContent>
                  </Tabs>
                </>
              )}
            </>
          )}

          {/* ===== STUDENTS ===== */}
          {section === 'students' && (
            <>
              <h1 className="font-heading text-2xl font-bold">Student Management</h1>
              <div className="grid sm:grid-cols-3 gap-4">
                {[
                  { label: 'Total Enrolled', value: '1,560', color: 'text-primary' },
                  { label: 'Active This Week', value: '892', color: 'text-green-600' },
                  { label: 'Completed', value: '312', color: 'text-amber-600' },
                ].map(m => (
                  <div key={m.label} className="bg-card rounded-xl border p-4 text-center">
                    <p className={`font-heading text-3xl font-bold ${m.color}`}>{m.value}</p>
                    <p className="text-xs text-muted-foreground mt-1">{m.label}</p>
                  </div>
                ))}
              </div>
              <div className="space-y-2">
                {courseStudents.map((s, i) => (
                  <div key={i} className="flex items-center gap-4 p-4 bg-card rounded-xl border">
                    <div className="w-10 h-10 rounded-full bg-primary/10 flex items-center justify-center font-bold text-primary">{s.name[0]}</div>
                    <div className="flex-1 min-w-0">
                      <p className="font-medium text-sm">{s.name}</p>
                      <p className="text-xs text-muted-foreground">{s.email} · {s.lastActive}</p>
                    </div>
                    <div className="hidden sm:flex flex-col w-32">
                      <div className="flex justify-between text-xs mb-1">
                        <span className="text-muted-foreground">Progress</span>
                        <span className="font-medium">{s.progress}%</span>
                      </div>
                      <div className="h-1.5 bg-muted rounded-full overflow-hidden">
                        <div className="h-full bg-primary rounded-full" style={{ width: `${s.progress}%` }} />
                      </div>
                    </div>
                    <Badge className={`text-xs border-0 flex-shrink-0 ${s.status === 'completed' ? 'bg-green-100 text-green-700' : s.status === 'inactive' ? 'bg-red-100 text-red-700' : 'bg-blue-100 text-blue-700'}`}>{s.status}</Badge>
                  </div>
                ))}
              </div>
            </>
          )}

          {/* ===== ANALYTICS ===== */}
          {section === 'analytics' && (
            <>
              <h1 className="font-heading text-2xl font-bold">Analytics & Reports</h1>
              <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
                {[
                  { label: 'Total Revenue', value: '₹55.3L', change: '+₹4.2L', color: 'text-green-600 bg-green-100', icon: DollarSign },
                  { label: 'Total Students', value: '1,560', change: '+48', color: 'text-primary bg-primary/10', icon: Users },
                  { label: 'Avg. Completion', value: '72%', change: '+5%', color: 'text-purple-600 bg-purple-100', icon: Award },
                  { label: 'Avg. Rating', value: '4.8', change: 'From 1,240', color: 'text-amber-600 bg-amber-100', icon: Star },
                ].map(s => {
                  const Icon = s.icon;
                  return (
                    <div key={s.label} className="bg-card rounded-xl border p-4">
                      <div className={`w-9 h-9 rounded-lg ${s.color} flex items-center justify-center mb-3`}><Icon className="w-4 h-4" /></div>
                      <p className="font-heading text-2xl font-bold">{s.value}</p>
                      <p className="text-xs text-muted-foreground">{s.label}</p>
                      <p className="text-xs text-green-600 mt-1">{s.change}</p>
                    </div>
                  );
                })}
              </div>
              <div className="grid lg:grid-cols-2 gap-6">
                <div className="bg-card rounded-xl border p-5">
                  <h3 className="font-heading font-semibold mb-4">Enrollment Trend</h3>
                  <div className="h-56">
                    <ResponsiveContainer width="100%" height="100%">
                      <BarChart data={enrollmentData}>
                        <CartesianGrid strokeDasharray="3 3" stroke="hsl(var(--border))" />
                        <XAxis dataKey="month" tick={{ fontSize: 11 }} />
                        <YAxis tick={{ fontSize: 11 }} />
                        <Tooltip />
                        <Bar dataKey="students" fill="hsl(var(--primary))" radius={[4, 4, 0, 0]} />
                      </BarChart>
                    </ResponsiveContainer>
                  </div>
                </div>
                <div className="bg-card rounded-xl border p-5">
                  <h3 className="font-heading font-semibold mb-4">Monthly Revenue (₹)</h3>
                  <div className="h-56">
                    <ResponsiveContainer width="100%" height="100%">
                      <LineChart data={revenueData}>
                        <CartesianGrid strokeDasharray="3 3" stroke="hsl(var(--border))" />
                        <XAxis dataKey="month" tick={{ fontSize: 11 }} />
                        <YAxis tick={{ fontSize: 11 }} tickFormatter={v => `₹${(v/1000).toFixed(0)}K`} />
                        <Tooltip formatter={v => [`₹${v.toLocaleString('en-IN')}`, 'Revenue']} />
                        <Line type="monotone" dataKey="revenue" stroke="hsl(var(--accent))" strokeWidth={2} />
                      </LineChart>
                    </ResponsiveContainer>
                  </div>
                </div>
                <div className="bg-card rounded-xl border p-5 lg:col-span-2">
                  <h3 className="font-heading font-semibold mb-4">Weekly Completion Rate</h3>
                  <div className="h-52">
                    <ResponsiveContainer width="100%" height="100%">
                      <AreaChart data={completionData}>
                        <CartesianGrid strokeDasharray="3 3" stroke="hsl(var(--border))" />
                        <XAxis dataKey="week" tick={{ fontSize: 11 }} />
                        <YAxis tick={{ fontSize: 11 }} domain={[60, 100]} />
                        <Tooltip formatter={v => `${v}%`} />
                        <Area type="monotone" dataKey="rate" stroke="hsl(var(--primary))" fill="hsl(var(--primary) / 0.1)" />
                      </AreaChart>
                    </ResponsiveContainer>
                  </div>
                </div>
              </div>
            </>
          )}

          {/* ===== NOTIFICATIONS ===== */}
          {section === 'notifications' && <InstructorNotifications />}

          {/* ===== SETTINGS ===== */}
          {section === 'settings' && (
            <>
              <h1 className="font-heading text-2xl font-bold">Settings</h1>
              <div className="grid sm:grid-cols-2 gap-6">
                <div className="bg-card rounded-xl border p-6 space-y-4">
                  <h3 className="font-semibold">Profile Information</h3>
                  <div className="space-y-3">
                    <div><Label>Display Name</Label><Input className="mt-1" defaultValue="Dr. Priya Sharma" /></div>
                    <div><Label>Bio / Title</Label><Input className="mt-1" defaultValue="AI Research Lead" /></div>
                    <div><Label>Email</Label><Input className="mt-1" defaultValue="priya@xceedge.in" /></div>
                  </div>
                  <Button className="gap-2"><Save className="w-4 h-4" /> Save Profile</Button>
                </div>
                <div className="bg-card rounded-xl border p-6 space-y-4">
                  <h3 className="font-semibold">Notification Preferences</h3>
                  {[
                    'New student enrollments', 'Course reviews & ratings',
                    'Assignment submissions', 'Platform announcements',
                    'Weekly performance report',
                  ].map(pref => (
                    <label key={pref} className="flex items-center justify-between">
                      <span className="text-sm">{pref}</span>
                      <input type="checkbox" defaultChecked className="w-4 h-4 accent-primary" />
                    </label>
                  ))}
                  <Button variant="outline" className="gap-2 w-full"><Save className="w-4 h-4" /> Save Preferences</Button>
                </div>
              </div>
            </>
          )}
        </motion.div>
      </div>

      {/* Create Course Slide-over */}
      <AnimatePresence>
        {showForm && (
          <>
            <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}
              className="fixed inset-0 bg-foreground/40 z-50" onClick={() => setShowForm(false)} />
            <motion.div initial={{ x: '100%' }} animate={{ x: 0 }} exit={{ x: '100%' }}
              className="fixed top-0 right-0 bottom-0 w-full max-w-lg bg-card z-50 border-l overflow-y-auto">
              <div className="p-6 space-y-5">
                <div className="flex items-center justify-between">
                  <h2 className="font-heading text-xl font-bold">Create New Course</h2>
                  <Button variant="ghost" size="icon" onClick={() => setShowForm(false)}><X className="w-5 h-5" /></Button>
                </div>
                <div className="grid sm:grid-cols-2 gap-4">
                  <div className="sm:col-span-2"><Label>Course Title</Label><Input className="mt-1" placeholder="e.g. Advanced React Patterns" /></div>
                  <div><Label>Category</Label>
                    <Select><SelectTrigger className="mt-1"><SelectValue placeholder="Select" /></SelectTrigger>
                      <SelectContent>
                        <SelectItem value="web">Web Development</SelectItem>
                        <SelectItem value="data">Data Science</SelectItem>
                        <SelectItem value="ai">AI & ML</SelectItem>
                        <SelectItem value="cyber">Cybersecurity</SelectItem>
                        <SelectItem value="cloud">Cloud</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                  <div><Label>Level</Label>
                    <Select><SelectTrigger className="mt-1"><SelectValue placeholder="Select" /></SelectTrigger>
                      {/* <SelectContent>
                        <SelectItem value="beginner">Beginner</SelectItem>
                        <SelectItem value="intermediate">Intermediate</SelectItem>
                        <SelectItem value="advanced">Advanced</SelectItem>
                      </SelectContent> */}
                    </Select>
                  </div>
                  <div><Label>Price (₹)</Label><Input type="number" className="mt-1" placeholder="29999" /></div>
                  <div><Label>Original Price (₹)</Label><Input type="number" className="mt-1" placeholder="49999" /></div>
                  <div className="sm:col-span-2"><Label>Duration</Label><Input className="mt-1" placeholder="e.g. 8 weeks" /></div>
                </div>
                <div><Label>Description</Label><Textarea className="mt-1 h-28" placeholder="Course description..." /></div>
                <div className="space-y-3">
                  <Label>Upload Materials</Label>
                  {[
                    { icon: Video, label: 'Upload Videos', accept: 'video/*' },
                    { icon: FileText, label: 'Upload Documents', accept: '.pdf,.doc,.docx' },
                  ].map(({ icon: Icon, label, accept }) => (
                    <label key={label} className="flex items-center gap-3 border-2 border-dashed rounded-lg p-4 cursor-pointer hover:bg-muted/40 transition-colors">
                      <Icon className="w-5 h-5 text-muted-foreground" />
                      <span className="text-sm text-muted-foreground">{label}</span>
                      <input type="file" accept={accept} className="hidden" multiple />
                    </label>
                  ))}
                </div>
                <div className="flex gap-3 pt-2">
                  <Button className="gap-2 flex-1"><CheckCircle2 className="w-4 h-4" /> Submit for Review</Button>
                  <Button variant="outline" onClick={() => setShowForm(false)}>Cancel</Button>
                </div>
              </div>
            </motion.div>
          </>
        )}
      </AnimatePresence>
    </div>
  );
}